import { WorkLocationTypeCodes } from '@Enum/index';
import { requestTypeOptionsAllowed } from '@Features/store/selectors/requestTypeOptionsAllowed';
import { RootState } from '@Store/index';
import {
  transformCompaniesDictToOptions,
  transformCompaniesDictToOptionsNum,
  transformDictToOptions,
  transformDictToOptionsNum,
  transformOrganizationsToOptions,
} from '@Utils/transformToOptions';
import { createSelector } from '@reduxjs/toolkit';

export const workLocationTypeAllowed: Record<number, boolean> = {
  [WorkLocationTypeCodes.DEPARTMENT]: true,
  [WorkLocationTypeCodes.PROJECT]: true,
};

const dictionary = (state: RootState) => state.common.dictionary;

const getRequisitionRequestTypeDictSelector = createSelector(
  dictionary,
  ({ requisitionRequestType }) => requisitionRequestType
);

const getRequestTypeDictSelector = createSelector(
  dictionary,
  ({ requestType }) => requestType
);

const getPurchaseStatusesDictSelector = createSelector(
  dictionary,
  ({ purchaseStatuses }) => purchaseStatuses
);

const getRequestTypeOptionLabels = createSelector(
  getRequestTypeDictSelector,
  (requestTypes) => {
    const filteredRequestTypes = requestTypes.filter(
      (requestType) => requestTypeOptionsAllowed[requestType.codeNum]
    );
    return filteredRequestTypes.map((item) => item.codeDesc);
  }
);

const getLeaveTypeDictSelector = createSelector(
  dictionary,
  ({ leaveType }) => leaveType
);

const getLeaveStatusesDictSelector = createSelector(
  dictionary,
  ({ leaveStatuses }) => leaveStatuses
);

const getITReqItemsDictSelector = createSelector(
  dictionary,
  ({ itReqItems }) => itReqItems
);

const getSIMCapValueDictSelector = createSelector(
  dictionary,
  ({ simCapValue }) => simCapValue
);

const getITAccessories = createSelector(
  dictionary,
  ({ itAccessories }) => itAccessories
);

const getITAccessoryOptions = createSelector(getITAccessories, (accessories) =>
  transformDictToOptions(accessories)
);

const getITReqStatusesSelector = createSelector(
  dictionary,
  ({ itReqStatuses }) => itReqStatuses
);

const getPassportTypeDictSelector = createSelector(
  dictionary,
  ({ passportType }) => passportType
);

const getPassportStatusesDictSelector = createSelector(
  dictionary,
  ({ passportStatuses }) => passportStatuses
);

const getRequisitionStatusesDictSelector = createSelector(
  dictionary,
  ({ requisitionStatuses }) => requisitionStatuses
);

const getSalaryTypeDictSelector = createSelector(
  dictionary,
  ({ salaryType }) => salaryType
);

const getSalaryStatusesDictSelector = createSelector(
  dictionary,
  ({ salaryStatuses }) => salaryStatuses
);

const getOverseasStatusesDictSelector = createSelector(
  dictionary,
  ({ overseasStatuses }) => overseasStatuses
);

const getCountriesDictSelector = createSelector(
  dictionary,
  ({ countries }) => countries
);

const getCountryLabels = createSelector(getCountriesDictSelector, (countries) =>
  countries.map((country) => country.countryName)
);

const getCompaniesDictSelector = createSelector(
  dictionary,
  ({ companies }) => companies
);

const getCompanyOptions = createSelector(
  getCompaniesDictSelector,
  (companies) => transformCompaniesDictToOptions(companies)
);

const getCompanyOptionsNum = createSelector(
  getCompaniesDictSelector,
  (companies) => transformCompaniesDictToOptionsNum(companies)
);

const getCompanyLabels = createSelector(getCompaniesDictSelector, (companies) =>
  companies.map((company) => company.compCode)
);

const getOverseasSponsorsDictSelector = createSelector(
  dictionary,
  ({ overseasSponsors }) => overseasSponsors
);

const getResumptionStatusesDict = createSelector(
  dictionary,
  ({ resumptionStatuses }) => resumptionStatuses
);

const getResumptionWorkShiftDict = createSelector(
  dictionary,
  ({ resumptionWorkShift }) => resumptionWorkShift
);

const getAttendanceStatusesDict = createSelector(
  dictionary,
  ({ attendanceStatuses }) => attendanceStatuses
);

const getAttendanceTypesDict = createSelector(
  dictionary,
  ({ attendanceType }) => attendanceType
);

const getAttendanceReasonsDict = createSelector(
  dictionary,
  ({ attendanceReason }) => attendanceReason
);

const getProjectNDepartmentsDict = createSelector(
  dictionary,
  ({ projectNDepartments }) => projectNDepartments
);

const getDepartmentsProjectsDict = createSelector(
  dictionary,
  ({ departmentsProjects }) => departmentsProjects
);

const getDepartmenProjectLabels = createSelector(
  getDepartmentsProjectsDict,
  (deptProjects) => deptProjects.map((item) => item.projDeptName)
);

const getWorkLocationTypes = createSelector(
  dictionary,
  ({ workLocationType }) => workLocationType
);

const getWorkLocationTypesAllowed = createSelector(
  getWorkLocationTypes,
  (workLocationTypes) =>
    workLocationTypes.filter(
      (workLocationType) => workLocationTypeAllowed[workLocationType.codeNum]
    )
);

const getWorkLocationOptions = createSelector(
  getWorkLocationTypesAllowed,
  (workLocationTypes) => transformDictToOptionsNum(workLocationTypes)
);

const getDepartmentProjectsList = createSelector(
  dictionary,
  ({ deptProjList }) => deptProjList
);

const getResignationStatusesDict = createSelector(
  dictionary,
  ({ resignationStatuses }) => resignationStatuses
);

const getResignationReasonsDict = createSelector(
  dictionary,
  ({ resignationReasons }) => resignationReasons
);

const getOrganizationsDict = createSelector(
  dictionary,
  ({ organizations }) => organizations
);

const getOrganizationOptions = createSelector(
  getOrganizationsDict,
  (organizations) => transformOrganizationsToOptions(organizations)
);

const getCostCenterTypeDict = createSelector(
  dictionary,
  ({ costCenterType }) => costCenterType
);

const getWarrantyTypeDict = createSelector(
  dictionary,
  ({ warrantyType }) => warrantyType
);

const getMrForDict = createSelector(dictionary, ({ mrForType }) => mrForType);

const getTenderForDict = createSelector(
  dictionary,
  ({ tenderForType }) => tenderForType
);

const getTenderModeDict = createSelector(
  dictionary,
  ({ tenderModeTypes }) => tenderModeTypes
);

const getTenderExtensionTypeDict = createSelector(
  dictionary,
  ({ tenderExtensionType }) => tenderExtensionType
);

const getRFQStatusDict = createSelector(
  dictionary,
  ({ rfqStatus }) => rfqStatus
);

const getDocumentTypesDict = createSelector(
  dictionary,
  ({ documentTypes }) => documentTypes
);

const getTenderDocumentTypesDict = createSelector(
  dictionary,
  ({ tenderDocumentTypes }) => tenderDocumentTypes
);

const getPurchaseClassifications = createSelector(
  dictionary,
  ({ purchaseClassifications }) => purchaseClassifications
);

const getCurrencies = createSelector(
  dictionary,
  ({ currencies }) => currencies
);

export {
  getCurrencies,
  getRequisitionRequestTypeDictSelector,
  getPurchaseClassifications,
  getCountriesDictSelector,
  getCountryLabels,
  getCompaniesDictSelector,
  getCompanyLabels,
  getOverseasSponsorsDictSelector,
  getRequestTypeOptionLabels,
  getRequestTypeDictSelector,
  getLeaveTypeDictSelector,
  getLeaveStatusesDictSelector,
  getITReqItemsDictSelector,
  getSIMCapValueDictSelector,
  getITAccessories,
  getITAccessoryOptions,
  getPassportTypeDictSelector,
  getPassportStatusesDictSelector,
  getRequisitionStatusesDictSelector,
  getSalaryTypeDictSelector,
  getSalaryStatusesDictSelector,
  getITReqStatusesSelector,
  getOverseasStatusesDictSelector,
  getResumptionStatusesDict,
  getResumptionWorkShiftDict,
  getAttendanceStatusesDict,
  getAttendanceTypesDict,
  getAttendanceReasonsDict,
  getWorkLocationTypes,
  getWorkLocationTypesAllowed,
  getCompanyOptions,
  getCompanyOptionsNum,
  getProjectNDepartmentsDict,
  getDepartmentsProjectsDict,
  getDepartmentProjectsList,
  getResignationStatusesDict,
  getResignationReasonsDict,
  getDepartmenProjectLabels,
  getWorkLocationOptions,
  getOrganizationsDict,
  getOrganizationOptions,
  getCostCenterTypeDict,
  getWarrantyTypeDict,
  getMrForDict,
  getTenderForDict,
  getTenderExtensionTypeDict,
  getRFQStatusDict,
  getDocumentTypesDict,
  getPurchaseStatusesDictSelector,
  getTenderDocumentTypesDict,
  getTenderModeDict,
};
